import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { STRINGS } from '@/strings/strings';
import { ApiError } from '@/lib/api-client';
import {
  listCoverAttempts,
  listSceneAttempts,
  pickCoverAttempt,
  pickSceneAttempt,
} from '../../api';
import type { AttemptListResponse } from '../../types';

/**
 * Pick-earlier-attempt slice (Sebastian ruling 2026-09-06) — one
 * component, both slots. Renders when the customer chooses "Ein
 * früheres Bild wählen" from the Change dialog's menu. Fetches the
 * attempts list on mount (signed URLs minted on the call), shows
 * images side-by-side with the active one marked, writes the pick
 * on click.
 *
 * ═══ ONE COMPONENT, TWO SLOTS ═══════════════════════════════════
 *
 * Sebastian ruling verbatim: "Same behaviour for scenes and for the
 * cover. Not two mechanics." The `kind` prop dispatches to
 * scene-side vs cover-side endpoints; the layout, the visual
 * marking, and the copy are identical.
 *
 * ═══ ACTIVE ID ═══════════════════════════════════════════════════
 *
 * Server returns `activeAttemptId` = the customer's pick when set,
 * else the newest accepted attempt id. Same "newest wins by default"
 * rule the print picker uses. The row whose `attemptId ===
 * activeAttemptId` renders the "Aktuell" badge.
 *
 * ═══ PICK IS SOFT ════════════════════════════════════════════════
 *
 * Clicking a non-active image POSTs the pick, then invokes
 * `onPicked()` which triggers the parent envelope refetch. A fresh
 * customer regen (from the other menu options) clears the pick
 * server-side; the customer never has to think in attempts.
 *
 * ═══ NO PER-IMAGE CAPTION, NO NUMBERING ══════════════════════════
 *
 * Sebastian ruling verbatim: "No per-image caption. Numbering
 * attempts makes the customer count, and whoever is happy should
 * never think in attempts." Images are equals; the only differentia
 * is the "Aktuell" badge on the current pick.
 */
export interface AttemptPickerDialogSceneProps {
  readonly kind: 'scene';
  readonly bookOrderId: string;
  readonly sceneId: string;
  readonly onPicked: () => void;
  readonly onClose: () => void;
}

export interface AttemptPickerDialogCoverProps {
  readonly kind: 'cover';
  readonly bookOrderId: string;
  readonly onPicked: () => void;
  readonly onClose: () => void;
}

export type AttemptPickerDialogProps =
  | AttemptPickerDialogSceneProps
  | AttemptPickerDialogCoverProps;

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: AttemptListResponse }
  | { kind: 'error'; message: string };

export function AttemptPickerDialog(props: AttemptPickerDialogProps) {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [picking, setPicking] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchList = async () => {
      try {
        const data =
          props.kind === 'scene'
            ? await listSceneAttempts(props.bookOrderId, props.sceneId)
            : await listCoverAttempts(props.bookOrderId);
        if (!cancelled) setState({ kind: 'ready', data });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError && typeof err.envelope?.user_message === 'string'
            ? err.envelope.user_message
            : STRINGS.book.review.editorSaveError;
        setState({ kind: 'error', message });
      }
    };
    void fetchList();
    return () => {
      cancelled = true;
    };
  }, [props]);

  const handlePick = useCallback(
    async (attemptId: string) => {
      setPicking(attemptId);
      try {
        if (props.kind === 'scene') {
          await pickSceneAttempt(props.bookOrderId, props.sceneId, attemptId);
        } else {
          await pickCoverAttempt(props.bookOrderId, attemptId);
        }
        props.onPicked();
      } catch (err) {
        const message =
          err instanceof ApiError && typeof err.envelope?.user_message === 'string'
            ? err.envelope.user_message
            : STRINGS.book.review.editorSaveError;
        setState({ kind: 'error', message });
        setPicking(null);
      }
    },
    [props],
  );

  return (
    <div className="space-y-4">
      <h2 id="page-change-dialog-heading" className="text-lg font-semibold text-dream-navy pr-6">
        {STRINGS.book.review.attemptPickHeading}
      </h2>

      {state.kind === 'loading' && (
        <div className="flex items-center justify-center py-8">
          <Loader2 aria-hidden className="h-6 w-6 animate-spin text-dream-navy/70" />
        </div>
      )}

      {state.kind === 'error' && (
        <p role="alert" className="text-sm text-error">
          {state.message}
        </p>
      )}

      {state.kind === 'ready' && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {state.data.attempts.map((a) => {
              const isActive = a.attemptId === state.data.activeAttemptId;
              const isPicking = picking === a.attemptId;
              return (
                <button
                  key={a.attemptId}
                  type="button"
                  onClick={() => (isActive || isPicking ? undefined : handlePick(a.attemptId))}
                  disabled={isActive || isPicking}
                  aria-pressed={isActive}
                  className={
                    'relative aspect-square overflow-hidden rounded-lg ring-2 transition-shadow ' +
                    (isActive
                      ? 'ring-dream-navy cursor-default'
                      : 'ring-dream-navy/20 hover:ring-dream-navy focus-visible:ring-dream-navy')
                  }
                >
                  <img
                    src={a.imageUrl}
                    alt=""
                    aria-hidden="true"
                    className="h-full w-full object-cover"
                  />
                  {isActive && (
                    <span className="absolute left-2 top-2 rounded-md bg-dream-navy px-2 py-0.5 text-xs font-semibold text-dream-white">
                      {STRINGS.book.review.attemptPickActiveBadge}
                    </span>
                  )}
                  {isPicking && (
                    <span className="absolute inset-0 flex items-center justify-center bg-dream-navy/30">
                      <Loader2 aria-hidden className="h-6 w-6 animate-spin text-dream-white" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-dream-navy/70">{STRINGS.book.review.attemptPickHint}</p>
        </>
      )}

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={props.onClose}
          className="h-10 rounded-lg border border-dream-navy/30 px-4 text-sm font-semibold text-dream-navy hover:bg-dream-navy/5"
        >
          {STRINGS.book.review.editorCancelButton}
        </button>
      </div>
    </div>
  );
}
